import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, FormOutlined } from '@ant-design/icons'
import { Button, Modal, notification, Space, Table, Tooltip } from 'antd'

import { Page, Total } from '../../../../../components'
import { BreadcrumbSystem as Breadcrumb, SysPermission } from '../../../../../constants'
import { announcementService } from '../../../../../services'
import { auth, formatter, validator } from '../../../../../util'

import 'antd/dist/antd.css'
import './styles.css'
import { announcementDate, announcementStatuses, recurrenceSummary, nextOccurrence } from '../../recurrence'

const defaultPageSize = 2
const { confirm } = Modal

/** Rule: Must define breadcrumb in every page **/
const breadcrumb = Object.freeze([
  Breadcrumb.HomeBase,
  Breadcrumb.AnnouncmentBase,
  Breadcrumb.AnnouncmentList
])

function getAnnouncementColumns (onDelete) {
  return Object.freeze([
    {
      title: 'Platform',
      dataIndex: 'platform_name',
      key: 'platform_name'
    },
    {
      title: 'Target',
      dataIndex: 'target_type',
      key: 'target_type',
      render: (targetType) => formatter.capitalize(targetType)
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (startDate) => announcementDate(startDate)
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (endDate) => announcementDate(endDate)
    },
    {
      title: 'Recurrence',
      key: 'recurrence',
      render: (_, item) => recurrenceSummary(item)
    },
    {
      title: 'Final End',
      dataIndex: 'schedule_end_date',
      key: 'schedule_end_date',
      render: announcementDate
    },
    {
      title: 'Next Occurrence',
      key: 'next_occurrence',
      render: (_, item) => nextOccurrence(item)
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: 'Short Description',
      dataIndex: 'short_description',
      key: 'short_description'
    },
    {
      title: 'Show Popup',
      dataIndex: 'show_popup',
      key: 'show_popup',
      render: (showPopup) => (
        <Tooltip title={showPopup ? 'Show as popup' : "Don't show as popup"}>
          {showPopup ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        </Tooltip>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => announcementStatuses[status] || status
    },
    {
      width: 2,
      render: (item) => (
        <Space size='middle'>
          {hasAccess(SysPermission.ORGANISATIONS.INFO.READ) ? (
            <Tooltip title='Edit'>
              <Link to={`/announcements/listing/${item.id}/info`}><FormOutlined /></Link>
            </Tooltip>
          ) : null}

          {hasAccess(SysPermission.ORGANISATIONS.INFO.DELETE) ? (
            <Tooltip title='Delete'>
              <Button type='link' onClick={() => onDelete(item)}><DeleteOutlined /></Button>
            </Tooltip>
          ) : null}
        </Space>
      )
    }
  ])
}

function hasAccess (permission) {
  return auth.hasAccess(permission)
}

function AnnouncementList () {
  const [filter, setFilter] = useState({})
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [total, setTotal] = useState(0)

  const listByPage = useCallback(({ filter, page, pageSize, searchText }) => {
    const newFilter = validator.isObject(filter) ? { ...filter } : {}
    const newPage = typeof page === 'number' ? page : 1
    const newPageSize = typeof pageSize === 'number' ? pageSize : defaultPageSize
    const newSearchText = typeof searchText === 'string' && searchText.trim.length > 0 ? searchText : undefined
    setLoading(true)
    return announcementService
      .listByPage(newPage, newPageSize, newFilter, {}, newSearchText)
      .then((response) => {
        if (validator.isObject(response)) {
          const { list, total } = response

          if (Array.isArray(list)) {
            setList(list)
          }

          if (typeof total === 'number') {
            setTotal(total)
          }
        }
      }).finally(() => {
        setFilter(newFilter)
        setLoading(false)
        setPage(newPage)
        setPageSize(newPageSize)
      })
  }, [])

  const changePage = useCallback((nextPage, nextSize) => {
    listByPage({ filter, page: nextPage, pageSize: nextSize }).catch(() => {
      notification.error({ message: 'Load Announcements not successful', description: 'Please try again.' })
    })
  }, [filter, listByPage])

  const removeAnnouncement = useCallback((item) => {
    confirm({
      title: 'Delete Announcement',
      content: 'Are you sure you want to delete this announcement?',
      onOk: async () => {
        setLoading(true)
        let response

        try {
          response = await announcementService.remove(item.id)
        } catch (error) {
          notification.error({
            message: 'Delete Announcement not successful',
            description: 'Announcement could not be deleted.'
          })
          setLoading(false)
          return
        }

        if (!response || !response.id) {
          notification.error({
            message: 'Delete Announcement not successful',
            description: 'Announcement could not be deleted.'
          })
          setLoading(false)
          return
        }

        notification.success({
          message: 'Delete Announcement successfully',
          description: 'Announcement was deleted successfully.'
        })

        try {
          await listByPage({ filter, page, pageSize })
        } catch (error) {
          notification.error({
            message: 'Refresh Announcement List not successful',
            description: 'The announcement was deleted, but the list could not be refreshed.'
          })
          setLoading(false)
        }
      }
    })
  }, [filter, listByPage, page, pageSize])

  useEffect(() => {
    let mounted = true

    if (!hasAccess(SysPermission.ORGANISATIONS.INFO.LIST)) {
      setLoading(false)
      return
    }

    announcementService
      .listByPage(1, defaultPageSize, {})
      .then((response) => {
        if (mounted) {
          if (validator.isObject(response)) {
            const { list, total } = response

            if (Array.isArray(list)) {
              setList(list)
            }

            if (typeof total === 'number') {
              setTotal(total)
            }
          }
        }
      }).finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [])

  return (
    <Page.Body>
      <Page.Breadcrumb item={breadcrumb} />

      <Page.Header title='Announcements'>
        <Link to='/announcements/listing/add'>
          <Button shape='round' type='primary'>Add Announcement</Button>
        </Link>
      </Page.Header>

      <Page.Content>
        <Table
          columns={getAnnouncementColumns(removeAnnouncement)}
          dataSource={list}
          loading={loading}
          pagination={{
            current: page,
            defaultPageSize,
            hideOnSinglePage: false,
            pageSize,
            total,
            onChange: changePage
          }}
          rowKey={({ id }) => `announcement-list|${id}`}
          size='small'
        />

        <Total total={total} title='announcements' />
      </Page.Content>
    </Page.Body>
  )
}

export default AnnouncementList
